import { useParams, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "@/components/ui/chart";
import {
    AreaChart,
    Area,
    ComposedChart,
    BarChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    ReferenceLine,
    Cell,
} from "recharts";
import {
    Pencil,
    Check,
    X,
    PackageOpen,
    Upload,
    Trash2,
    Tag,
    DollarSign,
    TrendingUp,
    Package,
    ArrowUpRight,
} from "lucide-react";
import { BarChart as BarChartIcon, Settings, FileText } from "lucide-react";

function PricingModelDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const searchButtonRef = useRef<HTMLButtonElement>(null);
    const [searchWidth, setSearchWidth] = useState<number | undefined>(
        undefined
    );

    // Log the ID
    console.log("Pricing Model ID:", id);

    // Mock data (fixed, not ID dependent)
    const mockData = {
        name: "Wood Sheds",
        active: true,
        createdAt: "2024-01-15",
        productCount: 42,
        results: {
            totalRevenue: 125000,
            totalRevenueTrend: 8.5,
            profitUplift: 12.5,
            profitUpliftTrend: 2.3,
            productsOptimized: 42,
            productsOptimizedTrend: 5,
            averagePriceChange: 3.2,
            averagePriceChangeTrend: 0.8,
        },
        parameters: {
            minPrice: 10.0,
            maxPrice: 100.0,
            targetMargin: 0.35,
            updateFrequency: "daily",
            competitionWeight: 0.4,
            demandWeight: 0.6,
        },
        log: [
            {
                timestamp: "2024-03-15 10:30:00",
                level: "info",
                message: "Price optimization completed for 42 products",
            },
            {
                timestamp: "2024-03-15 09:15:00",
                level: "info",
                message: "Starting daily optimization cycle",
            },
            {
                timestamp: "2024-03-14 18:45:00",
                level: "warning",
                message: "Price adjustment limit reached for product #1234",
            },
            {
                timestamp: "2024-03-14 10:30:00",
                level: "info",
                message: "Price optimization completed for 42 products",
            },
            {
                timestamp: "2024-03-14 09:15:00",
                level: "info",
                message: "Starting daily optimization cycle",
            },
        ],
    };

    // Generate pricing curve data (unimodal, skewed)
    const generatePricingCurveData = () => {
        const optimalPrice = 89.99;
        const currentPrice = 79.99;
        const data = [];

        for (let price = 40; price <= 140; price += 2) {
            // Unimodal skewed curve - profit per visitor
            const distanceFromOptimal = Math.abs(price - optimalPrice);
            // Skewed to the right (higher prices have steeper decline)
            const skewFactor = price > optimalPrice ? 1.5 : 0.8;
            const profitPerVisitor =
                50 *
                Math.exp(-Math.pow(distanceFromOptimal * skewFactor, 2) / 800);

            data.push({
                price,
                profitPerVisitor: Math.max(0, profitPerVisitor),
            });
        }

        return { data, optimalPrice, currentPrice };
    };

    // Generate demand curve data
    const generateDemandCurveData = () => {
        const data = [];

        for (let price = 40; price <= 140; price += 2) {
            // Demand decreases as price increases (inverse relationship)
            const baseDemand = 1000;
            const priceElasticity = 2.5;
            const demand = Math.max(
                0,
                baseDemand * Math.pow(price / 50, -priceElasticity)
            );

            data.push({
                price,
                demand: Math.round(demand),
            });
        }

        return data;
    };

    const pricingCurve = generatePricingCurveData();
    const demandCurveData = generateDemandCurveData();

    // Generate profit per visitor time series data (noisy with steps)
    const generateProfitPerVisitorTimeSeriesData = () => {
        const data = [];
        const days = 30; // Last 30 days
        const step1Day = Math.floor(days * (2 / 8)); // Around 2/8s
        const step2Day = Math.floor(days * (6 / 8)); // Around 6/8s

        // Calculate the dates for price changes
        const priceChangeDate1 = new Date();
        priceChangeDate1.setDate(
            priceChangeDate1.getDate() - (days - step1Day)
        );
        const priceChangeDate1Label = priceChangeDate1.toLocaleDateString(
            "en-US",
            {
                month: "short",
                day: "numeric",
            }
        );

        const priceChangeDate2 = new Date();
        priceChangeDate2.setDate(
            priceChangeDate2.getDate() - (days - step2Day)
        );
        const priceChangeDate2Label = priceChangeDate2.toLocaleDateString(
            "en-US",
            {
                month: "short",
                day: "numeric",
            }
        );

        for (let day = 0; day < days; day++) {
            // Base PPV value
            let basePpv = 0.6;

            // Step increases
            if (day >= step1Day) {
                basePpv = 0.85; // First step increase
            }
            if (day >= step2Day) {
                basePpv = 0.98; // Second step increase
            }

            // Add noise (random variation)
            const noise = (Math.random() - 0.5) * 0.05;

            const profitPerVisitor = Math.max(
                0.5,
                Math.min(1.2, basePpv + noise)
            );

            // Format date for display
            const date = new Date();
            date.setDate(date.getDate() - (days - day));
            const dateLabel = date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
            });

            data.push({
                date: dateLabel,
                day: day,
                profitPerVisitor: Number(profitPerVisitor.toFixed(2)),
            });
        }

        return {
            data,
            priceChangeDate1: priceChangeDate1Label,
            priceChangeDate2: priceChangeDate2Label,
        };
    };

    const profitPerVisitorData = generateProfitPerVisitorTimeSeriesData();
    const profitPerVisitorTimeSeriesData = profitPerVisitorData.data;

    // Generate Pareto curve data for contribution margin
    const generateParetoCurveData = () => {
        const data = [];
        let cumulativeShare = 0;

        // Generate percentiles from 0 to 100 in 5% increments
        for (let percentile = 0; percentile <= 100; percentile += 5) {
            // Pareto distribution: contribution share decreases as percentile increases
            // Top percentiles (0-5%) contribute much more than bottom percentiles (95-100%)
            const normalizedPercentile = percentile / 100;

            // Individual contribution share at this percentile
            // Using power law: higher percentiles have exponentially lower contributions
            // Top 5% contribute ~15%, bottom 5% contribute ~0.5%
            const contributionShare =
                15 * Math.pow(1 - normalizedPercentile, 2.2);

            // Cumulative share: sum of all contributions up to this percentile
            cumulativeShare += contributionShare;

            data.push({
                percentile,
                contributionShare: Number(contributionShare.toFixed(2)),
                cumulativeShare: Number(cumulativeShare.toFixed(2)),
            });
        }

        // Normalize cumulative share to reach 100% at percentile 100
        const maxCumulative = data[data.length - 1].cumulativeShare;
        if (maxCumulative > 0) {
            return data.map((item) => ({
                ...item,
                cumulativeShare: Number(
                    ((item.cumulativeShare / maxCumulative) * 100).toFixed(2)
                ),
            }));
        }

        return data;
    };

    const paretoCurveData = generateParetoCurveData();

    // Generate mock data for top winners and losers
    const generateWinnersLosersData = () => {
        const products = [
            { name: "Premium Wood Shed - 12x16", margin: 12500 },
            { name: "Deluxe Summer House", margin: 9800 },
            { name: "Premium Fence Panel Set", margin: 8700 },
            { name: "Garden Shed - 8x10", margin: 7500 },
            { name: "Wooden Fence Post Pack", margin: 6800 },
            { name: "Plastic Garden Chair Set", margin: 4200 },
            { name: "Basic Fence Panel", margin: 3100 },
            { name: "Economy Garden Shed", margin: 2500 },
            { name: "Starter Fence Kit", margin: 1800 },
            { name: "Small Wood Shed", margin: 1200 },
            { name: "Budget Fence Panel", margin: -500 },
            { name: "Clearance Garden Chair", margin: -800 },
            { name: "Discontinued Shed Model", margin: -1200 },
            { name: "Old Stock Fence Panel", margin: -1500 },
            { name: "Loss Leader Shed", margin: -2100 },
        ];

        // Sort by margin
        const sorted = [...products].sort((a, b) => b.margin - a.margin);

        // Get top 5 winners and bottom 5 losers
        const winners = sorted.slice(0, 5); // Top 5 winners (highest first)
        const losers = sorted.slice(-5); // Bottom 5 losers (most negative at the end, so biggest loser at bottom)

        return { winners, losers };
    };

    const { winners, losers } = generateWinnersLosersData();

    // Combine winners and losers for the chart (winners first at top, then losers at bottom)
    const winnersLosersChartData = [
        ...winners.map((item) => ({
            name: item.name,
            margin: item.margin,
            type: "winner",
        })),
        ...losers.map((item) => ({
            name: item.name,
            margin: item.margin,
            type: "loser",
        })),
    ];

    const chartConfig = {
        profitPerVisitor: {
            label: "Profit per Visitor",
            color: "hsl(var(--primary))",
        },
        demand: {
            label: "Demand (Units)",
            color: "hsl(var(--primary))",
        },
        date: {
            label: "Date",
            color: "hsl(var(--muted-foreground))",
        },
        contributionShare: {
            label: "Margin Contribution Share",
            color: "hsl(var(--primary))",
        },
        cumulativeShare: {
            label: "Cumulative Share",
            color: "hsl(var(--chart-2))",
        },
        margin: {
            label: "Margin Contribution ($)",
            color: "hsl(var(--primary))",
        },
    };

    // Mock data for attached SKUs
    const attachedSkus = [
        {
            id: "SKU-001",
            name: "Premium Wood Shed - 8x10",
            variant: "Natural Cedar",
            price: 1299.99,
            addedAt: "2024-01-20",
        },
        {
            id: "SKU-002",
            name: "Premium Wood Shed - 10x12",
            variant: "Natural Cedar",
            price: 1799.99,
            addedAt: "2024-01-20",
        },
        {
            id: "SKU-003",
            name: "Plastic Garden Chair Set",
            variant: "White - 4 Pack",
            price: 89.99,
            addedAt: "2024-02-15",
        },
        {
            id: "SKU-004",
            name: "Premium Fence Panel",
            variant: "6ft x 6ft - Oak",
            price: 149.99,
            addedAt: "2024-02-18",
        },
    ];

    // Mock data for available products (for search)
    const availableProducts = [
        { id: "PROD-001", name: "Premium Wood Shed - 8x10", sku: "SKU-001" },
        { id: "PROD-002", name: "Premium Wood Shed - 10x12", sku: "SKU-002" },
        { id: "PROD-003", name: "Plastic Garden Chair Set", sku: "SKU-003" },
        { id: "PROD-004", name: "Premium Fence Panel", sku: "SKU-004" },
        { id: "PROD-005", name: "Summer House - 12x10", sku: "SKU-005" },
        { id: "PROD-006", name: "Garden Bench - Teak", sku: "SKU-006" },
        { id: "PROD-007", name: "Garden Table Set", sku: "SKU-007" },
        { id: "PROD-008", name: "Outdoor Storage Box", sku: "SKU-008" },
    ];

    // Filter products based on search query
    const filteredProducts = availableProducts.filter(
        (product) =>
            !attachedSkus.some((sku) => sku.id === product.sku) &&
            (product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                product.sku.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleAddSku = (product: (typeof availableProducts)[0]) => {
        // TODO: Add SKU to pricing model
        console.log("Adding SKU:", product);
        setSearchOpen(false);
        setSearchQuery("");
    };

    const handleRemoveSku = (skuId: string) => {
        // TODO: Remove SKU from pricing model
        console.log("Removing SKU:", skuId);
    };

    const handleBulkUpload = () => {
        // TODO: Implement bulk upload
        console.log("Bulk upload clicked");
    };

    // Update popover width to match button width
    useEffect(() => {
        if (searchButtonRef.current) {
            setSearchWidth(searchButtonRef.current.offsetWidth);
        }
    }, [searchOpen]);

    // Use edited name if available, otherwise use mock data name
    const displayName = editedName || mockData.name;

    const handleEditClick = () => {
        setEditedName(displayName);
        setIsEditing(true);
    };

    const handleSave = () => {
        // TODO: Save to database
        console.log("Saving name:", editedName);
        setIsEditing(false);
        // For now, just keep it in state (cache)
    };

    const handleCancel = () => {
        setEditedName("");
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleSave();
        } else if (e.key === "Escape") {
            handleCancel();
        }
    };

    return (
        <>
            <header className="flex h-16 shrink-0 items-center gap-2 border-b">
                <div className="flex items-center gap-2 px-4">
                    <SidebarTrigger className="-ml-1" />
                    <Separator
                        orientation="vertical"
                        className="mr-2 data-[orientation=vertical]:h-4"
                    />
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate("/pricing-models");
                                    }}
                                >
                                    Pricing Models
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                {isEditing ? (
                                    <div className="flex items-center gap-2">
                                        <Input
                                            value={editedName}
                                            onChange={(e) =>
                                                setEditedName(e.target.value)
                                            }
                                            onKeyDown={handleKeyDown}
                                            onBlur={handleSave}
                                            className="h-7 w-64"
                                            autoFocus
                                        />
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={handleSave}
                                        >
                                            <Check className="h-3 w-3" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={handleCancel}
                                        >
                                            <X className="h-3 w-3" />
                                        </Button>
                                    </div>
                                ) : (
                                    <BreadcrumbPage>
                                        {displayName}
                                    </BreadcrumbPage>
                                )}
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                    {!isEditing && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleEditClick}
                            className="h-8 w-8"
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </header>
            <div className="flex flex-1 flex-col gap-4 p-4">
                <Tabs defaultValue="results" className="w-full">
                    <TabsList className="w-full p-0 bg-background justify-start border-b rounded-none">
                        <TabsTrigger
                            value="results"
                            className="rounded-none bg-background h-full data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary px-4 py-2"
                        >
                            <div className="flex items-center gap-2">
                                <BarChartIcon className="h-4 w-4" />
                                <span>Results</span>
                            </div>
                        </TabsTrigger>
                        <TabsTrigger
                            value="skus"
                            className="rounded-none bg-background h-full data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary px-4 py-2"
                        >
                            <div className="flex items-center gap-2">
                                <PackageOpen className="h-4 w-4" />
                                <span>SKUs</span>
                            </div>
                        </TabsTrigger>
                        <TabsTrigger
                            value="parameters"
                            className="rounded-none bg-background h-full data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary px-4 py-2"
                        >
                            <div className="flex items-center gap-2">
                                <Settings className="h-4 w-4" />
                                <span>Parameters</span>
                            </div>
                        </TabsTrigger>
                        <TabsTrigger
                            value="log"
                            className="rounded-none bg-background h-full data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-primary px-4 py-2"
                        >
                            <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                <span>Log</span>
                            </div>
                        </TabsTrigger>
                    </TabsList>

                    <div className="mt-6">
                        <TabsContent value="results" className="space-y-3">
                            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium">
                                            Total Revenue
                                        </CardTitle>
                                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-xl font-bold">
                                            $
                                            {mockData.results.totalRevenue.toLocaleString()}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            <span className="text-green-500 flex items-center gap-1">
                                                <ArrowUpRight className="h-3 w-3" />
                                                +
                                                {
                                                    mockData.results
                                                        .totalRevenueTrend
                                                }
                                                %
                                            </span>
                                        </p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium">
                                            Profit Uplift
                                        </CardTitle>
                                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-xl font-bold">
                                            +{mockData.results.profitUplift}%
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            <span className="text-green-500 flex items-center gap-1">
                                                <ArrowUpRight className="h-3 w-3" />
                                                +
                                                {
                                                    mockData.results
                                                        .profitUpliftTrend
                                                }
                                                %
                                            </span>
                                        </p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium">
                                            Products Optimized
                                        </CardTitle>
                                        <Package className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-xl font-bold">
                                            {mockData.results.productsOptimized}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            <span className="text-green-500 flex items-center gap-1">
                                                <ArrowUpRight className="h-3 w-3" />
                                                +
                                                {
                                                    mockData.results
                                                        .productsOptimizedTrend
                                                }
                                            </span>
                                        </p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium">
                                            Avg Price Change
                                        </CardTitle>
                                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-xl font-bold">
                                            {mockData.results
                                                .averagePriceChange > 0
                                                ? "+"
                                                : ""}
                                            {
                                                mockData.results
                                                    .averagePriceChange
                                            }
                                            %
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            <span className="text-green-500 flex items-center gap-1">
                                                <ArrowUpRight className="h-3 w-3" />
                                                +
                                                {
                                                    mockData.results
                                                        .averagePriceChangeTrend
                                                }
                                                %
                                            </span>
                                        </p>
                                    </CardContent>
                                </Card>
                            </div>
                            <div className="grid gap-3 grid-cols-1 md:grid-cols-3">
                                <Card className="md:col-span-1">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-base">
                                            Price vs Profit per Visitor
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ChartContainer
                                            config={chartConfig}
                                            className="h-[220px] w-full"
                                        >
                                            <AreaChart
                                                data={pricingCurve.data}
                                                margin={{
                                                    top: 5,
                                                    right: 10,
                                                    left: 20,
                                                    bottom: 5,
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey="price"
                                                    tickCount={3}
                                                    interval="preserveStartEnd"
                                                    label={{
                                                        value: "Price ($)",
                                                        position:
                                                            "insideBottom",
                                                        offset: -5,
                                                    }}
                                                />
                                                <YAxis
                                                    tickCount={4}
                                                    width={45}
                                                    label={{
                                                        value: "Profit per Visitor ($)",
                                                        angle: -90,
                                                        position: "insideLeft",
                                                        offset: -10,
                                                        style: {
                                                            textAnchor:
                                                                "middle",
                                                        },
                                                    }}
                                                />
                                                <ChartTooltip
                                                    content={
                                                        <ChartTooltipContent />
                                                    }
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="profitPerVisitor"
                                                    stroke="hsl(var(--primary))"
                                                    strokeWidth={2}
                                                    fill="hsl(var(--primary))"
                                                    fillOpacity={0.4}
                                                    dot={false}
                                                />
                                                <ReferenceLine
                                                    x={
                                                        pricingCurve.optimalPrice
                                                    }
                                                    stroke="hsl(var(--chart-3))"
                                                    strokeDasharray="5 5"
                                                    label={{
                                                        value: "Optimal",
                                                        position: "top",
                                                    }}
                                                />
                                                <ReferenceLine
                                                    x={
                                                        pricingCurve.currentPrice
                                                    }
                                                    stroke="hsl(var(--destructive))"
                                                    strokeDasharray="3 3"
                                                    label={{
                                                        value: "Current",
                                                        position: "top",
                                                    }}
                                                />
                                            </AreaChart>
                                        </ChartContainer>
                                    </CardContent>
                                </Card>
                                <Card className="md:col-span-1">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-base">
                                            Price vs Demand
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ChartContainer
                                            config={chartConfig}
                                            className="h-[220px] w-full"
                                        >
                                            <AreaChart
                                                data={demandCurveData}
                                                margin={{
                                                    top: 5,
                                                    right: 10,
                                                    left: 20,
                                                    bottom: 5,
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey="price"
                                                    tickCount={3}
                                                    interval="preserveStartEnd"
                                                    label={{
                                                        value: "Price ($)",
                                                        position:
                                                            "insideBottom",
                                                        offset: -5,
                                                    }}
                                                />
                                                <YAxis
                                                    tickCount={4}
                                                    width={45}
                                                    label={{
                                                        value: "Demand (Units)",
                                                        angle: -90,
                                                        position: "insideLeft",
                                                        offset: -10,
                                                        style: {
                                                            textAnchor:
                                                                "middle",
                                                        },
                                                    }}
                                                />
                                                <ChartTooltip
                                                    content={
                                                        <ChartTooltipContent />
                                                    }
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="demand"
                                                    stroke="hsl(var(--primary))"
                                                    strokeWidth={2}
                                                    fill="hsl(var(--primary))"
                                                    fillOpacity={0.4}
                                                    dot={false}
                                                />
                                                <ReferenceLine
                                                    x={
                                                        pricingCurve.currentPrice
                                                    }
                                                    stroke="hsl(var(--destructive))"
                                                    strokeDasharray="3 3"
                                                    label={{
                                                        value: "Current",
                                                        position: "top",
                                                    }}
                                                />
                                            </AreaChart>
                                        </ChartContainer>
                                    </CardContent>
                                </Card>
                                <Card className="md:col-span-1">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-base">
                                            Profit per Visitor (30 days)
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ChartContainer
                                            config={chartConfig}
                                            className="h-[220px] w-full"
                                        >
                                            <AreaChart
                                                data={
                                                    profitPerVisitorTimeSeriesData
                                                }
                                                margin={{
                                                    top: 20,
                                                    right: 10,
                                                    left: 20,
                                                    bottom: 0,
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey="date"
                                                    tickCount={4}
                                                    interval={1}
                                                    angle={-45}
                                                    textAnchor="end"
                                                    height={50}
                                                />
                                                <YAxis
                                                    tickCount={10}
                                                    domain={[0, 1.3]}
                                                    width={45}
                                                    label={{
                                                        value: "Profit per visitor (£)",
                                                        angle: -90,
                                                        style: {
                                                            textAnchor:
                                                                "middle",
                                                        },
                                                        position: "insideLeft",
                                                        offset: -10,
                                                    }}
                                                />
                                                <ChartTooltip
                                                    content={
                                                        <ChartTooltipContent />
                                                    }
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="profitPerVisitor"
                                                    stroke="hsl(var(--primary))"
                                                    strokeWidth={2}
                                                    fill="hsl(var(--primary))"
                                                    fillOpacity={0.4}
                                                    dot={false}
                                                />
                                                <ReferenceLine
                                                    x={
                                                        profitPerVisitorData.priceChangeDate1
                                                    }
                                                    stroke="hsl(var(--muted-foreground))"
                                                    strokeDasharray="3 3"
                                                    label={({
                                                        viewBox,
                                                    }: any) => {
                                                        if (viewBox) {
                                                            return (
                                                                <g>
                                                                    <foreignObject
                                                                        x={
                                                                            viewBox.x -
                                                                            8
                                                                        }
                                                                        y={
                                                                            viewBox.y -
                                                                            20
                                                                        }
                                                                        width={
                                                                            16
                                                                        }
                                                                        height={
                                                                            16
                                                                        }
                                                                    >
                                                                        <div className="flex items-center justify-center">
                                                                            <Tag className="h-4 w-4 text-muted-foreground" />
                                                                        </div>
                                                                    </foreignObject>
                                                                </g>
                                                            );
                                                        }
                                                        return <g />;
                                                    }}
                                                />
                                                <ReferenceLine
                                                    x={
                                                        profitPerVisitorData.priceChangeDate2
                                                    }
                                                    stroke="hsl(var(--muted-foreground))"
                                                    strokeDasharray="3 3"
                                                    label={({
                                                        viewBox,
                                                    }: any) => {
                                                        if (viewBox) {
                                                            return (
                                                                <g>
                                                                    <foreignObject
                                                                        x={
                                                                            viewBox.x -
                                                                            8
                                                                        }
                                                                        y={
                                                                            viewBox.y -
                                                                            20
                                                                        }
                                                                        width={
                                                                            16
                                                                        }
                                                                        height={
                                                                            16
                                                                        }
                                                                    >
                                                                        <div className="flex items-center justify-center">
                                                                            <Tag className="h-4 w-4 text-muted-foreground" />
                                                                        </div>
                                                                    </foreignObject>
                                                                </g>
                                                            );
                                                        }
                                                        return <g />;
                                                    }}
                                                />
                                            </AreaChart>
                                        </ChartContainer>
                                    </CardContent>
                                </Card>
                            </div>
                            <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-base">
                                            Contribution Margin Pareto
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ChartContainer
                                            config={chartConfig}
                                            className="h-[220px] w-full"
                                        >
                                            <ComposedChart
                                                data={paretoCurveData}
                                                margin={{
                                                    top: 5,
                                                    right: 10,
                                                    left: 10,
                                                    bottom: 5,
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey="percentile"
                                                    tickCount={3}
                                                    interval="preserveStartEnd"
                                                    label={{
                                                        value: "Percentile (%)",
                                                        position:
                                                            "insideBottom",
                                                        offset: -5,
                                                    }}
                                                />
                                                <YAxis
                                                    yAxisId="left"
                                                    tickCount={4}
                                                    width={45}
                                                    label={{
                                                        value: "Contribution Share (%)",
                                                        angle: -90,
                                                        position: "insideLeft",
                                                        offset: 10,
                                                        style: {
                                                            textAnchor:
                                                                "middle",
                                                        },
                                                    }}
                                                />
                                                <YAxis
                                                    yAxisId="right"
                                                    orientation="right"
                                                    tickCount={4}
                                                    width={45}
                                                    label={{
                                                        value: "Cumulative Share (%)",
                                                        angle: 90,
                                                        position: "insideRight",
                                                        offset: 10,
                                                        style: {
                                                            textAnchor:
                                                                "middle",
                                                        },
                                                    }}
                                                />
                                                <ChartTooltip
                                                    content={
                                                        <ChartTooltipContent />
                                                    }
                                                />
                                                <Area
                                                    yAxisId="right"
                                                    type="monotone"
                                                    dataKey="cumulativeShare"
                                                    stroke="hsl(var(--primary))"
                                                    strokeWidth={2}
                                                    fill="hsl(var(--primary))"
                                                    fillOpacity={0}
                                                    dot={false}
                                                />
                                                <Line
                                                    yAxisId="left"
                                                    type="monotone"
                                                    dataKey="contributionShare"
                                                    stroke="hsl(var(--chart-2))"
                                                    strokeWidth={2}
                                                    dot={false}
                                                />
                                            </ComposedChart>
                                        </ChartContainer>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-base">
                                            Top 5 Winners & Losers
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ChartContainer
                                            config={chartConfig}
                                            className="h-[220px] w-full"
                                        >
                                            <BarChart
                                                data={winnersLosersChartData}
                                                layout="vertical"
                                                margin={{
                                                    top: 5,
                                                    right: 10,
                                                    left: 10,
                                                    bottom: 5,
                                                }}
                                            >
                                                <CartesianGrid
                                                    strokeDasharray="3 3"
                                                    horizontal={true}
                                                    vertical={false}
                                                />
                                                <XAxis
                                                    type="number"
                                                    tickCount={4}
                                                    label={{
                                                        value: "Margin Contribution ($)",
                                                        position:
                                                            "insideBottom",
                                                        offset: -5,
                                                    }}
                                                />
                                                <YAxis
                                                    type="category"
                                                    dataKey="name"
                                                    width={85}
                                                    tick={{ fontSize: 11 }}
                                                />
                                                <ChartTooltip
                                                    content={
                                                        <ChartTooltipContent
                                                            formatter={(
                                                                value
                                                            ) =>
                                                                `$${Number(
                                                                    value
                                                                ).toLocaleString()}`
                                                            }
                                                        />
                                                    }
                                                />
                                                <ReferenceLine
                                                    x={0}
                                                    stroke="hsl(var(--muted-foreground))"
                                                    strokeDasharray="3 3"
                                                />
                                                <Bar
                                                    dataKey="margin"
                                                    radius={[0, 4, 4, 0]}
                                                >
                                                    {winnersLosersChartData.map(
                                                        (entry, index) => (
                                                            <Cell
                                                                key={`cell-${index}`}
                                                                fill={
                                                                    entry.margin >
                                                                    0
                                                                        ? "hsl(var(--primary))"
                                                                        : "hsl(var(--chart-2))"
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </Bar>
                                            </BarChart>
                                        </ChartContainer>
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>

                        <TabsContent value="skus" className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Manage SKUs</CardTitle>
                                    <CardDescription>
                                        Add or remove products from this pricing
                                        model
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center gap-2">
                                        <Popover
                                            open={searchOpen}
                                            onOpenChange={setSearchOpen}
                                        >
                                            <PopoverTrigger asChild>
                                                <Button
                                                    ref={searchButtonRef}
                                                    variant="outline"
                                                    role="combobox"
                                                    aria-expanded={searchOpen}
                                                    className="flex-1 justify-between"
                                                >
                                                    <span className="text-muted-foreground">
                                                        Search products to
                                                        add...
                                                    </span>
                                                    <PackageOpen className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent
                                                className="p-0"
                                                align="start"
                                                style={{
                                                    width: searchWidth
                                                        ? `${searchWidth}px`
                                                        : undefined,
                                                }}
                                            >
                                                <Command>
                                                    <CommandInput
                                                        placeholder="Search products or SKUs..."
                                                        value={searchQuery}
                                                        onValueChange={
                                                            setSearchQuery
                                                        }
                                                    />
                                                    <CommandList>
                                                        <CommandEmpty>
                                                            No products found.
                                                        </CommandEmpty>
                                                        <CommandGroup>
                                                            {filteredProducts.map(
                                                                (product) => (
                                                                    <CommandItem
                                                                        key={
                                                                            product.id
                                                                        }
                                                                        onSelect={() =>
                                                                            handleAddSku(
                                                                                product
                                                                            )
                                                                        }
                                                                    >
                                                                        <PackageOpen className="mr-2 h-4 w-4" />
                                                                        <div className="flex flex-col">
                                                                            <span>
                                                                                {
                                                                                    product.name
                                                                                }
                                                                            </span>
                                                                            <span className="text-xs text-muted-foreground">
                                                                                {
                                                                                    product.sku
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                    </CommandItem>
                                                                )
                                                            )}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                        <Button
                                            variant="outline"
                                            onClick={handleBulkUpload}
                                        >
                                            <Upload className="h-4 w-4 mr-2" />
                                            Bulk Upload
                                        </Button>
                                    </div>

                                    <div className="rounded-md border">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>SKU</TableHead>
                                                    <TableHead>
                                                        Product Name
                                                    </TableHead>
                                                    <TableHead>
                                                        Variant
                                                    </TableHead>
                                                    <TableHead className="text-right">
                                                        Price
                                                    </TableHead>
                                                    <TableHead>Added</TableHead>
                                                    <TableHead className="text-right">
                                                        Actions
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {attachedSkus.length > 0 ? (
                                                    attachedSkus.map((sku) => (
                                                        <TableRow key={sku.id}>
                                                            <TableCell className="font-medium">
                                                                {sku.id}
                                                            </TableCell>
                                                            <TableCell>
                                                                {sku.name}
                                                            </TableCell>
                                                            <TableCell>
                                                                {sku.variant}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                $
                                                                {sku.price.toLocaleString(
                                                                    undefined,
                                                                    {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    }
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {new Date(
                                                                    sku.addedAt
                                                                ).toLocaleDateString()}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() =>
                                                                        handleRemoveSku(
                                                                            sku.id
                                                                        )
                                                                    }
                                                                >
                                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={6}
                                                            className="h-24 text-center"
                                                        >
                                                            No SKUs added yet.
                                                            Search for products
                                                            to add them.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="parameters" className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Model Parameters</CardTitle>
                                    <CardDescription>
                                        Configure the settings for this pricing
                                        model
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="text-sm font-medium">
                                                Minimum Price
                                            </label>
                                            <p className="text-sm text-muted-foreground">
                                                ${mockData.parameters.minPrice}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">
                                                Maximum Price
                                            </label>
                                            <p className="text-sm text-muted-foreground">
                                                ${mockData.parameters.maxPrice}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">
                                                Target Margin
                                            </label>
                                            <p className="text-sm text-muted-foreground">
                                                {(
                                                    mockData.parameters
                                                        .targetMargin * 100
                                                ).toFixed(0)}
                                                %
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">
                                                Update Frequency
                                            </label>
                                            <p className="text-sm text-muted-foreground capitalize">
                                                {
                                                    mockData.parameters
                                                        .updateFrequency
                                                }
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">
                                                Competition Weight
                                            </label>
                                            <p className="text-sm text-muted-foreground">
                                                {(
                                                    mockData.parameters
                                                        .competitionWeight * 100
                                                ).toFixed(0)}
                                                %
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium">
                                                Demand Weight
                                            </label>
                                            <p className="text-sm text-muted-foreground">
                                                {(
                                                    mockData.parameters
                                                        .demandWeight * 100
                                                ).toFixed(0)}
                                                %
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="log" className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Activity Log</CardTitle>
                                    <CardDescription>
                                        Recent events and operations for this
                                        pricing model
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {mockData.log.map((entry, index) => (
                                            <div
                                                key={index}
                                                className="flex items-start gap-3 p-3 rounded-md border"
                                            >
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs text-muted-foreground">
                                                            {entry.timestamp}
                                                        </span>
                                                        <Badge
                                                            variant={
                                                                entry.level ===
                                                                "warning"
                                                                    ? "destructive"
                                                                    : "secondary"
                                                            }
                                                            className="text-xs"
                                                        >
                                                            {entry.level}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm">
                                                        {entry.message}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </div>
                </Tabs>
            </div>
        </>
    );
}

export default PricingModelDetail;
